import { z } from "zod";
import { colomboBusinessDate, hasPermission, type Permission, type Role } from "../core";
import type { Tx } from "../store";
import { DEFAULT_SETTINGS, type Alert, type AuditLog, type Settings, type User } from "../types";

export type Actor = { id: string; name: string; role: Role; permissions: Permission[] };
export type Ctx = {
  tx: Tx; actor: Actor; now: Date; iso: string; businessDate: string; correlationId: string;
  /** Set while pricing a quote: nothing is committed, so approvals are not demanded yet. */
  dryRun?: boolean;
  /** Audit entries raised by the command, written together and chained by `flushAudits`. */
  audits: AuditLog[];
};

export type ErrorCode = "unauthenticated" | "permission-denied" | "invalid-argument" | "failed-precondition" | "not-found";
export class DomainError extends Error {
  /** Work that must persist even though the command itself is rolled back (for example a failed-PIN counter). */
  afterRollback?: (tx: Tx) => Promise<void>;
  constructor(readonly code: ErrorCode, message: string) { super(message); this.name = "DomainError"; }
}
export function fail(code: ErrorCode, message: string): never { throw new DomainError(code, message); }

export const newId = () => crypto.randomUUID();
export const can = (actor: Actor, permission: Permission) => hasPermission(actor.role, permission, actor.permissions);
export function requirePermission(ctx: Ctx, permission: Permission): void {
  if (!can(ctx.actor, permission)) fail("permission-denied", "You do not have permission for this action.");
}

export function parse<S extends z.ZodType>(schema: S, input: unknown): z.infer<S> {
  const result = schema.safeParse(input);
  if (result.success) return result.data;
  const issue = result.error.issues[0];
  fail("invalid-argument", issue ? `${issue.path.join(".") || "Input"}: ${issue.message}` : "Invalid input");
}

export function makeCtx(tx: Tx, actor: Actor, now: Date, correlationId: string = newId()): Ctx {
  return { tx, actor, now, iso: now.toISOString(), businessDate: colomboBusinessDate(now), correlationId, audits: [] };
}

export function audit(ctx: Ctx, action: string, targetType: string, targetId: string, summary: Record<string, unknown> = {}, extra: { reason?: string; approvedBy?: string } = {}): void {
  ctx.audits.push({
    id: newId(), actorId: ctx.actor.id, actorName: ctx.actor.name, actorRole: ctx.actor.role, action, targetType, targetId,
    summary, reason: extra.reason ?? "", approvedBy: extra.approvedBy ?? "", correlationId: ctx.correlationId,
    createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
}

/** "LKR 12,500.00" for alert texts, which are read on a phone's lock screen. */
export const lkr = (minor: number) => `LKR ${minor < 0 ? "-" : ""}${Math.floor(Math.abs(minor) / 100).toLocaleString("en-US")}.${String(Math.abs(minor) % 100).padStart(2, "0")}`;
export function raiseAlert(ctx: Ctx, kind: Alert["kind"], title: string, body: string, orderId: string | null = null): void {
  if (ctx.dryRun) return;
  ctx.tx.set("alerts", { id: newId(), kind, title, body, orderId, actorName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1 });
}
export const sha256Hex = async (text: string) => Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text))), (byte) => byte.toString(16).padStart(2, "0")).join("");

/** Deterministic JSON: object keys sorted, so the same entry hashes identically wherever it was stored. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`).join(",")}}`;
  return JSON.stringify(value ?? null);
}
export async function auditHash(log: AuditLog): Promise<string> {
  const text = stableStringify([log.seq, log.prevHash, log.id, log.actorId, log.actorName, log.actorRole, log.action, log.targetType, log.targetId, log.summary, log.reason, log.approvedBy, log.correlationId, log.createdAt]);
  return Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text))), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Writes the command's audit entries as links in one hash chain: each entry carries the hash of
 * the one before it. Editing or removing any past entry breaks every hash after it, which
 * `verifyAuditChain` detects. The chain head lives in a backend-only counter document.
 */
export async function flushAudits(ctx: Ctx): Promise<void> {
  if (ctx.audits.length === 0) return;
  const head = await ctx.tx.get("orderCounters", "auditChain");
  let seq = head?.sequence ?? 0, prevHash = head?.hash ?? "";
  for (const entry of ctx.audits.splice(0)) {
    seq += 1;
    const log: AuditLog = { ...entry, seq, prevHash };
    log.hash = prevHash = await auditHash(log);
    ctx.tx.set("auditLogs", log);
  }
  ctx.tx.set("orderCounters", { id: "auditChain", businessDate: "", type: "audit", sequence: seq, hash: prevHash });
}

export type ChainCheck = { checked: number; unchained: number; problem?: { log: AuditLog; reason: "altered" | "missing-before" } };
/** Checks a set of audit entries (any order). Gaps are only reported between entries that are both present. */
export async function verifyAuditChain(logs: readonly AuditLog[]): Promise<ChainCheck> {
  const chained = logs.filter((log) => log.seq !== undefined).sort((a, b) => a.seq! - b.seq!);
  let previous: AuditLog | undefined;
  for (const log of chained) {
    if (await auditHash(log) !== log.hash) return { checked: chained.length, unchained: logs.length - chained.length, problem: { log, reason: "altered" } };
    if (previous && (log.seq !== previous.seq! + 1 || log.prevHash !== previous.hash)) return { checked: chained.length, unchained: logs.length - chained.length, problem: { log, reason: "missing-before" } };
    previous = log;
  }
  return { checked: chained.length, unchained: logs.length - chained.length };
}

export async function getSettings(tx: Tx): Promise<Settings> {
  const stored = await tx.get("settings", "restaurant");
  return stored ? { ...DEFAULT_SETTINGS, ...stored, thresholds: { ...DEFAULT_SETTINGS.thresholds, ...stored.thresholds } } : DEFAULT_SETTINGS;
}

const encoder = new TextEncoder();
const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const fromBase64 = (text: string) => Uint8Array.from(atob(text), (char) => char.charCodeAt(0));

/** PBKDF2-SHA256 via WebCrypto, available in browsers, Electron and Node 20+. */
export async function hashSecret(secret: string, salt?: string, iterations = 150_000): Promise<{ hash: string; salt: string }> {
  const saltBytes = salt ? fromBase64(salt) : crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.importKey("raw", encoder.encode(secret), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: saltBytes, iterations }, key, 256);
  return { hash: toBase64(new Uint8Array(bits)), salt: toBase64(saltBytes) };
}
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let index = 0; index < a.length; index++) difference |= a.charCodeAt(index) ^ b.charCodeAt(index);
  return difference === 0;
}

/**
 * Rounds for recovery codes. The server (a Cloudflare Worker) refuses more than 100,000, and a 20-character random code
 * needs no slow hashing to be safe: the rounds only protect guessable secrets such as passwords and PINs.
 */
export const RECOVERY_ITERATIONS = 20_000;
/** Rounds for newly set PINs. Kept modest so the check fits a serverless CPU budget; a 4–8 digit PIN is protected by the attempt lock-out, not by hashing cost. */
export const PIN_ITERATIONS = 10_000;
export const ApprovalSchema = z.object({ approverId: z.string().min(1).max(128), pin: z.string().regex(/^\d{4,8}$/, "PIN must be 4 to 8 digits") });
export type Approval = z.infer<typeof ApprovalSchema>;
export type Approver = { id: string; name: string; role: Role };

/**
 * Returns who authorised a sensitive action. The acting user is accepted when they hold the
 * permission (and pass the optional threshold check); otherwise a second staff member must
 * authorise with their PIN. PINs lock for five minutes after five consecutive failures.
 */
export async function verifyApprover(ctx: Ctx, approval: Approval | undefined, permission: Permission, allows: (user: { role: Role }) => boolean = () => true): Promise<Approver> {
  if (ctx.dryRun || (can(ctx.actor, permission) && allows(ctx.actor))) return { id: ctx.actor.id, name: ctx.actor.name, role: ctx.actor.role };
  if (!approval) fail("permission-denied", "Manager approval is required for this action.");
  const user = await ctx.tx.get("users", approval.approverId) as User | undefined;
  if (!user || user.disabled || !hasPermission(user.role, permission, user.permissions) || !allows(user)) {
    fail("permission-denied", "The selected approver cannot authorise this action.");
  }
  const secret = await ctx.tx.get("staffSecrets", user.id);
  if (!secret?.pinHash || !secret.pinSalt) fail("failed-precondition", "The approver has not set an approval PIN.");
  if (secret.pinLockedUntil && secret.pinLockedUntil > ctx.iso) fail("permission-denied", "Approval PIN is temporarily locked. Try again in a few minutes.");
  const { hash } = await hashSecret(approval.pin, secret.pinSalt, secret.pinIterations ?? 60_000);
  if (!constantTimeEqual(hash, secret.pinHash)) {
    const error = new DomainError("permission-denied", "Incorrect approval PIN.");
    error.afterRollback = async (tx) => {
      const current = await tx.get("staffSecrets", user.id);
      if (!current) return;
      const attempts = (current.failedPinAttempts ?? 0) + 1;
      const locked = attempts >= 5;
      tx.set("staffSecrets", { ...current, failedPinAttempts: locked ? 0 : attempts, ...(locked ? { pinLockedUntil: new Date(ctx.now.getTime() + 5 * 60_000).toISOString() } : {}) });
      const failure = makeCtx(tx, ctx.actor, ctx.now, ctx.correlationId);
      audit(failure, "approval.pin_failed", "user", user.id, { permission, locked });
      await flushAudits(failure);
    };
    throw error;
  }
  if (secret.failedPinAttempts) ctx.tx.set("staffSecrets", { ...secret, failedPinAttempts: 0 });
  return { id: user.id, name: user.displayName, role: user.role };
}
